import React, { Component } from 'react'
import { Trans } from '@lingui/react'
import { connect } from 'react-redux'
import { withI18n, withI18nProps } from '@lingui/react'
import compose from 'lodash/fp/compose'
import join from 'lodash/fp/join'
import map from 'lodash/fp/map'
import slice from 'lodash/fp/slice'
import get from 'lodash/get'

import prepareComponent from 'client/shared/decorators/prepare-component'
import errorBoundary, {
  ErrorBoundaryProps,
} from 'client/shared/decorators/error-boundary'
import env from 'env'

import { loadWorksByRoleAndType } from 'client/bookmate/reducers/author-reducer'
import {
  mapStateToPropsForAuthor,
  renderAuthorWorks,
  getSectionId,
  MappedAuthorProps,
} from 'client/bookmate/helpers/author-helpers'

import MetaTitle from 'client/shared/blocks/meta-title'
import { MetaDescription } from 'client/shared/blocks/meta-description'
import { LayoutColumn } from 'client/shared/blocks/layout-column'
import InfiniteScroll from 'client/shared/blocks/infinite-scroll'
import Spacer from 'client/shared/blocks/spacer'

import { Dispatch } from 'shared/types/redux'
import { Params } from 'client/shared/types/react-router'
import type { Exact } from 'types/local/exact-check'
import isEmpty from 'lodash/isEmpty'
import List from '../blocks/list/list'

const ALLOWED_ROLES = [
  'author',
  'translator',
  'narrator',
  'illustrator',
  'publisher',
]
const ALLOWED_WORK_TYPES = ['books', 'audiobooks', 'comicbooks', 'series']

type Props = (Exact<MappedAuthorProps> & {
  dispatch: Dispatch
  params: Params
}) &
  ErrorBoundaryProps &
  withI18nProps

const getTitlesStr = compose(join(', '), map('title'), slice(0, 20))

type State = {
  reset: boolean
}

class AuthorWorksBox extends Component<Props, State> {
  sectionTitles = {
    author_books: 'authored_books',
    author_audiobooks: 'authored_audiobooks',
    author_comicbooks: 'authored_comicbooks',
    translator_books: 'translated_books',
    narrator_audiobooks: 'narrated_audiobooks',
    illustrator_comicbooks: 'illustrated_comicbooks',
  }

  state = {
    reset: false,
  }

  getContentPage() {
    const {
      params: { role, worksType },
    } = this.props

    return this.props.pages[worksType][role]
  }

  getHeading(): JSX.Element | null {
    const {
      params: { role, worksType },
    } = this.props
    const messageId = getSectionId(role, worksType)

    if (!messageId) return null

    return <Trans id={messageId} />
  }

  load = (): null => {
    const {
      dispatch,
      params: { authorId, role, worksType },
      pages,
    } = this.props

    const page = pages[worksType][role]

    if (!authorId || !role || !worksType) return null

    return dispatch(
      loadWorksByRoleAndType({
        authorId,
        role,
        worksType,
        page,
        per_page: 20,
        append: true,
      }),
    )
  }

  onSortingSelect({ order_by, order_direction }) {
    const {
      dispatch,
      params: { role, worksType, authorId },
    } = this.props

    this.setState({ reset: !this.state.reset })

    dispatch(
      loadWorksByRoleAndType({
        authorId,
        role,
        worksType,
        page: 1,
        per_page: 20,
        append: false,
        order_by,
        order_direction,
      }),
    )
  }

  render(): JSX.Element {
    const {
      params: { role, worksType },
    } = this.props

    return (
      <>
        {this.renderMeta()}
        <InfiniteScroll
          load={this.load}
          page={this.getContentPage()}
          reset={this.state.reset}
        >
          <LayoutColumn>
            <List reverseHeader withSorting={this.onSortingSelect.bind(this)}>
              {renderAuthorWorks(this.props, {
                role,
                worksType,
                itemsType: 'list',
              })}
              <Spacer size={32} />
            </List>
          </LayoutColumn>
        </InfiniteScroll>
      </>
    )
  }

  renderMeta(): JSX.Element | null {
    const {
      author,
      params: { worksType, role },
      i18n,
    } = this.props

    if (isEmpty(author)) return null

    const { name = '', works_count = 0 } = author

    let title: string
    let description: string
    const titlesStr = getTitlesStr(get(this.props, [worksType, role]))
    const descriptionParams = {
      [role]: name,
      number: works_count,
      list: titlesStr,
    }
    switch (role) {
      case 'narrator':
        title = i18n._('author.meta_title_narrator', { narrator: name })
        description = i18n._(
          'author.meta_description_narrator',
          descriptionParams,
        )
        break
      case 'publisher':
        title = i18n._(`author.meta_title_publisher_works_${worksType}`, {
          publisher: name,
        })
        description = i18n._(
          `author.meta_description_publisher_works_${worksType}`,
          descriptionParams,
        )
        break
      default:
        title = i18n._(`author.meta_title_works_${worksType}`, { author: name })
        description = i18n._(
          `author.meta_description_works_${worksType}`,
          descriptionParams,
        )
    }

    const ogDescription = i18n._(
      'author.meta_description_works_og',
      descriptionParams,
    )

    return (
      <>
        <MetaTitle disableSuffix hasOgSuffix title={title} og={name} />
        <MetaDescription description={description} og={ogDescription} />
      </>
    )
  }
}

const prepareWrapper = prepareComponent(async function ({ store, params }) {
  const { authorId, role, worksType } = params
  const { dispatch } = store

  if (!authorId || !role || !worksType) return null

  if (
    env.isServer() &&
    (!ALLOWED_ROLES.includes(role) || !ALLOWED_WORK_TYPES.includes(worksType))
  ) {
    throw { response: { status: 404 } }
  }

  await dispatch(
    loadWorksByRoleAndType({
      authorId,
      role,
      worksType,
      page: 1,
      per_page: 20,
      append: false,
    }),
  )
})

const connectWrapper = connect(mapStateToPropsForAuthor)

const wrappers = compose(
  prepareWrapper,
  connectWrapper,
  errorBoundary,
  withI18n({ update: true }),
)

export default wrappers(AuthorWorksBox)
